import { createSelector, select } from '@ngrx/store';
import { combineLatest, map, Observable, of, pipe, switchMap } from 'rxjs';

import { Registration, RegistrationType } from 'app/features/manual-input-registration/models';
import { getActivityCode } from 'app/features/scan-flow/utils/scanned-code-list.utils';
import { ActivityType } from 'app/shared/features/activity-selector/models';
import { LocationType } from 'app/shared/features/location-selector/models';
import {
    createRowRegistrationFromRowSelection,
    RowRegistration,
} from 'app/shared/features/map-row-selector/models';
import { filterRowsSelectionByScannedCodes } from 'app/shared/features/map-row-selector/utils';
import { selectIsOnBreakIfLoaded } from 'app/state/break/break.selectors';

import { getScannedCodeNumbersByCodeType } from '../../../utils';
import {
    getRegistrationType,
    isScanFlowRegistrationDataComplete,
    isScanFlowRegistrationDataInProgress,
} from '../scan-flow-labor-registration/scan-flow-labor-registration.utils';
import { isResultRegistrationActivity } from '../scan-flow-result-registration/scan-flow-result-registration.utils';
import { ScanFlowRegistrationData } from '../scan-flow.reducer';
import {
    mapScanFlowCodesToActivity,
    mapScanFlowCodesToBatch,
    mapScanFlowCodesToListDefinitions,
    selectScanFlowRegistrationData,
    selectScanFlowState,
    selectScanFlowWaitingForUserInput,
} from '../scan-flow.selectors';

const selectScanFlowIsAutoRegistrationEnabled = (store: Observable<object>) =>
    combineLatest([
        store.pipe(select(selectScanFlowWaitingForUserInput)),
        store.pipe(selectIsOnBreakIfLoaded),
    ]).pipe(
        map(([waitingForUserInput, isOnBreak]) => {
            return !waitingForUserInput && !isOnBreak;
        }),
    );

export const selectScheduledRegistrationDate = createSelector(
    selectScanFlowState,
    (state) => state.scheduledRegistrationDate,
);

const mapScanFlowRegistrationDataToRowRegistrations = ({
    scannedCodes,
    rowSelection,
}: ScanFlowRegistrationData) => {
    const filteredRowSelection = filterRowsSelectionByScannedCodes({ scannedCodes, rowSelection });
    return filteredRowSelection.map(createRowRegistrationFromRowSelection);
};

const mapScanFlowRegistrationDataToLocationsRegistrations = ({
    scannedCodes,
    rowSelection,
}: ScanFlowRegistrationData) => {
    const filteredRowSelection = filterRowsSelectionByScannedCodes({
        scannedCodes,
        rowSelection,
    });
    if (!filteredRowSelection || filteredRowSelection.length < 1) return undefined;
    return {
        locationNumber: filteredRowSelection[0].rowNumber,
        registrationStatus: 'done',
    };
};

export const selectScanFlowRegistrationDataCompleteness = (store: Observable<object>) =>
    store.pipe(
        select(selectScanFlowRegistrationData),
        switchMap((registrationData) =>
            store.pipe(mapToScanFlowRegistrationDataCompleteness(registrationData)),
        ),
    );

// Check data completeness during the scan flow process
// It returns an observable that emits 'complete', 'in-progress', or 'empty'
// For registrationData, there's an additional check to see if the data is complete or in progress in
export const mapToScanFlowRegistrationDataCompleteness = (
    registrationData: ScanFlowRegistrationData | undefined,
) =>
    !registrationData
        ? () => of('empty')
        : pipe(
              mapScanFlowCodesToListDefinitions(registrationData.scannedCodes),
              map((scanCodeListDefinitions) => {
                  const isComplete = isScanFlowRegistrationDataComplete({
                      registrationData,
                      scanCodeListDefinitions,
                  });
                  if (isComplete) {
                      return 'complete';
                  }

                  const isInProgress = isScanFlowRegistrationDataInProgress({
                      registrationData,
                      scanCodeListDefinitions,
                  });
                  if (isInProgress) {
                      return 'in-progress';
                  }

                  return 'empty';
              }),
          );

export const transformScanFlowRegistrationData =
    ({
        registrationData,
        registrationTimestamp,
    }: {
        registrationData: ScanFlowRegistrationData;
        registrationTimestamp: number;
    }) =>
    (store: Observable<object>) =>
        combineLatest([
            store.pipe(mapScanFlowCodesToActivity(registrationData.scannedCodes)),
            store.pipe(mapScanFlowCodesToBatch(registrationData.scannedCodes)),
            store.pipe(mapToScanFlowRegistrationDataCompleteness(registrationData)),
        ]).pipe(
            map(([activity, batch, registrationDataCompleteness]) => {
                // Check if result registration activity and adjust completeness accordingly
                // Result registration data can be empty since it has been sent in container measurement
                // Therefore, we consider it complete in that case
                const isRegistrationDataComplete =
                    isResultRegistrationActivity(activity) && registrationDataCompleteness === 'empty'
                        ? 'complete'
                        : registrationDataCompleteness;

                // If employee number is missing, activity is missing or registration data is empty, we cannot create a registration
                if (!registrationData.employeeNumber || !activity || isRegistrationDataComplete === 'empty') {
                    return undefined;
                }

                // For activity with location type row, we set value of row
                // For activity with all other location type than row, we set value of location
                let rowRegistrations: RowRegistration[] = [];
                let locationRegistrations = undefined;
                if (activity.locationType === LocationType.Row)
                    rowRegistrations = mapScanFlowRegistrationDataToRowRegistrations(registrationData);
                else
                    locationRegistrations =
                        mapScanFlowRegistrationDataToLocationsRegistrations(registrationData);

                // For result registration activities, we only need the activity number, batch number, employee number, location and registration timestamp
                // Other fields are not available in the activity upon submission
                if (isResultRegistrationActivity(activity)) {
                    const registration: Registration = {
                        activityNumber: activity.number,
                        amountResult: undefined,
                        batchNumber: batch?.number ?? undefined,
                        containerNumbers: [],
                        employeeNumber: registrationData.employeeNumber,
                        productBoxRegistrations: [],
                        registrationType: RegistrationType.Labour,
                        rowRegistrations,
                        locationRegistrations,
                        registrationTimestamp,
                    };

                    return registration;
                }

                const activityName = getActivityCode(registrationData.scannedCodes)?.code || '';
                const isEndBreakActivity = activity.activityType === ActivityType.Break;
                const registrationType = getRegistrationType(isEndBreakActivity ? 'endbreak' : activityName);

                const registration: Registration = {
                    activityNumber: activity.number,
                    amountResult: registrationData.amountResult,
                    batchNumber: batch?.number ?? undefined,
                    containerNumbers: getScannedCodeNumbersByCodeType(
                        registrationData.scannedCodes,
                        'container',
                    ),
                    employeeNumber: registrationData.employeeNumber,
                    productBoxRegistrations: registrationData.productBoxRegistrations,
                    registrationType,
                    rowRegistrations,
                    locationRegistrations,
                    registrationTimestamp,
                };

                return registration;
            }),
        );

export const selectScanFlowAutoRegistrationAndCompleteness = (store: Observable<object>) =>
    combineLatest([
        store.pipe(selectScanFlowRegistrationDataCompleteness),
        store.pipe(selectScanFlowIsAutoRegistrationEnabled),
    ]).pipe(
        map(([registrationDataCompleteness, isAutoRegistrationEnabled]) => ({
            registrationDataCompleteness,
            isAutoRegistrationEnabled,
        })),
    );
